import { useMemo } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle } from "@ionic/react";
import { Check, Moon, Eye, Inbox } from "lucide-react";
import { useStore } from "../store/useStore";
import { selectStaleTasks } from "../lib/stale";
import type { ITask } from "../types";

function ageLabel(task: ITask): string {
  const days = Math.floor((Date.now() - new Date(task.created_at).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "1 day old";
  return `${days} days old`;
}

function TriageCard({
  task,
  onDone,
  onSnooze,
  onKeep,
}: {
  task: ITask;
  onDone: () => void;
  onSnooze: (days: number) => void;
  onKeep: () => void;
}) {
  const title = task.title || task.raw_input;
  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-sm font-medium text-foreground break-words">{title}</div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">{ageLabel(task)}</span>
      </div>
      {task.description && (
        <p className="text-xs text-muted-foreground line-clamp-3">{task.description}</p>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          onClick={onDone}
          className="btn-primary flex items-center gap-1.5 text-xs"
        >
          <Check className="w-3.5 h-3.5" />
          Done
        </button>
        <button
          onClick={() => onSnooze(1)}
          className="btn-secondary flex items-center gap-1.5 text-xs"
        >
          <Moon className="w-3.5 h-3.5" />
          Snooze 1d
        </button>
        <button
          onClick={() => onSnooze(3)}
          className="btn-secondary flex items-center gap-1.5 text-xs"
        >
          <Moon className="w-3.5 h-3.5" />
          3d
        </button>
        <button
          onClick={() => onSnooze(7)}
          className="btn-secondary flex items-center gap-1.5 text-xs"
        >
          <Moon className="w-3.5 h-3.5" />
          7d
        </button>
        <button
          onClick={onKeep}
          className="btn-secondary flex items-center gap-1.5 text-xs"
        >
          <Eye className="w-3.5 h-3.5" />
          Keep
        </button>
      </div>
    </div>
  );
}

export default function TriagePage() {
  const tasks = useStore((state) => state.tasks);
  const completeTask = useStore((state) => state.completeTask);
  const snoozeTask = useStore((state) => state.snoozeTask);
  const markNudged = useStore((state) => state.markNudged);

  const stale = useMemo(() => selectStaleTasks(tasks), [tasks]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Triage</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-4 space-y-3">
          {stale.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <Inbox className="w-10 h-10 text-muted-foreground/50" />
              <div className="text-sm font-medium text-foreground">All caught up</div>
              <div className="text-xs text-muted-foreground max-w-xs">
                Nothing has been sitting in your backlog long enough to pester you about. Keep dumping.
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs text-muted-foreground">
                {stale.length} task{stale.length === 1 ? "" : "s"} waiting on you.
              </div>
              {stale.map((task) => (
                <TriageCard
                  key={task.uuid}
                  task={task}
                  onDone={() => completeTask(task.uuid)}
                  onSnooze={(days) => snoozeTask(task.uuid, days)}
                  onKeep={() => markNudged(task.uuid)}
                />
              ))}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
